import { GripVertical } from "lucide-react";
import {
  Group as GroupPrimitive,
  Panel as PanelPrimitive,
  Separator as SeparatorPrimitive,
} from "react-resizable-panels";

import { cn } from "@/lib/utils";

/**
 * Thin shadcn-style wrapper around `react-resizable-panels` v4.
 *
 * v4 renamed the public API: `PanelGroup` → `Group`, `PanelResizeHandle` →
 * `Separator`, `Panel` unchanged. Keep the shadcn-style names (`ResizablePanelGroup`
 * / `ResizablePanel` / `ResizableHandle`) so call sites read naturally.
 *
 * ```tsx
 * <ResizablePanelGroup direction="horizontal">
 *   <ResizablePanel defaultSize={25} minSize={12} maxSize={45}>sidebar</ResizablePanel>
 *   <ResizableHandle withHandle />
 *   <ResizablePanel>main</ResizablePanel>
 * </ResizablePanelGroup>
 * ```
 */
function ResizablePanelGroup({
  direction,
  className,
  ...props
}: Omit<React.ComponentProps<typeof GroupPrimitive>, "orientation"> & {
  direction?: "horizontal" | "vertical";
}) {
  return (
    <GroupPrimitive
      orientation={direction ?? "horizontal"}
      data-slot="resizable-panel-group"
      className={cn("flex h-full w-full data-[orientation=vertical]:flex-col", className)}
      {...props}
    />
  );
}

const ResizablePanel = PanelPrimitive;

function ResizableHandle({
  withHandle,
  className,
  ...props
}: React.ComponentProps<typeof SeparatorPrimitive> & {
  withHandle?: boolean;
}) {
  return (
    <SeparatorPrimitive
      data-slot="resizable-handle"
      className={cn(
        // Visible separator is 1px; pseudo-element extends the hit area to
        // 8px (4px either side) so the cursor actually catches the handle
        // without pixel-sniper precision. col-resize cursor makes the
        // affordance obvious on hover.
        "relative flex w-px items-center justify-center bg-border cursor-col-resize",
        "after:absolute after:inset-y-0 after:left-1/2 after:w-2 after:-translate-x-1/2",
        "hover:bg-primary/40 data-[dragging=true]:bg-primary/60",
        "data-[orientation=vertical]:h-px data-[orientation=vertical]:w-full data-[orientation=vertical]:cursor-row-resize",
        "data-[orientation=vertical]:after:left-0 data-[orientation=vertical]:after:h-2 data-[orientation=vertical]:after:w-full data-[orientation=vertical]:after:-translate-y-1/2 data-[orientation=vertical]:after:translate-x-0",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        className,
      )}
      {...props}
    >
      {withHandle && (
        <div className="z-10 flex h-4 w-3 items-center justify-center rounded-sm border border-border bg-border">
          <GripVertical className="size-2.5" />
        </div>
      )}
    </SeparatorPrimitive>
  );
}

export { ResizableHandle, ResizablePanel, ResizablePanelGroup };
